import { motion } from "framer-motion";
import {
  Home as HomeIcon,
  Trees,
  DoorOpen,
  Wrench,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  TrendingUp,
  Euro,
} from "lucide-react";
import QuickLeadPanel from "../QuickLeadPanel";

export const DOMAINS = [
  { key: "facade", label: "Façade & Enduits", icon: HomeIcon },
  { key: "exterieur", label: "Aménagement extérieur", icon: Trees },
  { key: "menuiseries", label: "Menuiseries", icon: DoorOpen },
  { key: "toiture", label: "Toiture & Gouttières", icon: Wrench },
];

export const STATUS_CONFIG = {
  bon: {
    label: "Bon état",
    icon: CheckCircle2,
    className: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30",
    dot: "bg-emerald-400",
  },
  surveiller: {
    label: "À surveiller",
    icon: AlertCircle,
    className: "text-amber-400 bg-amber-500/10 border-amber-500/30",
    dot: "bg-amber-400",
  },
  urgent: {
    label: "Urgent",
    icon: AlertTriangle,
    className: "text-red-400 bg-red-500/10 border-red-500/30",
    dot: "bg-red-400",
  },
};

export function scoreColor(score) {
  if (score >= 75) return "#10B981"; // emerald
  if (score >= 50) return "#F59E0B"; // amber
  return "#EF4444"; // red
}

export function formatEuro(n) {
  if (!n && n !== 0) return "—";
  return `${n.toLocaleString("fr-FR")} €`;
}

function ScoreCircle({ score, color }) {
  const radius = 42;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  return (
    <div className="relative w-28 h-28 shrink-0" data-testid="diagnosis-score">
      <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
        <circle
          cx="50"
          cy="50"
          r={radius}
          stroke="rgba(255,255,255,0.08)"
          strokeWidth="7"
          fill="none"
        />
        <circle
          cx="50"
          cy="50"
          r={radius}
          stroke={color}
          strokeWidth="7"
          fill="none"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{ transition: "stroke-dashoffset 1s ease-out" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="font-display text-3xl text-white leading-none">{score}</div>
        <div className="text-[10px] uppercase tracking-wider text-neutral-500 mt-1">
          /100
        </div>
      </div>
    </div>
  );
}

function EstimateCard({ icon: Icon, label, range, accent, testId }) {
  const min = range?.min;
  const max = range?.max;
  return (
    <div
      data-testid={testId}
      className="rounded-2xl border border-white/10 bg-black/30 p-5"
    >
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} className={accent} />
        <span className="text-xs uppercase tracking-wider text-neutral-400">{label}</span>
      </div>
      <div className={`font-display text-2xl ${accent} leading-tight`}>
        {formatEuro(min)} <span className="text-neutral-500 text-base">–</span>{" "}
        {formatEuro(max)}
      </div>
    </div>
  );
}

function DomainCard({ domain, data }) {
  const cfg = STATUS_CONFIG[data.status] || STATUS_CONFIG.surveiller;
  return (
    <div
      data-testid={`diagnosis-domain-${domain.key}`}
      className="rounded-2xl border border-white/10 bg-black/30 p-5"
    >
      <div className="flex items-center gap-2 mb-2">
        <domain.icon size={16} className="text-[#FF5A00]" />
        <span className="text-sm font-medium text-white">{domain.label}</span>
      </div>
      <div
        className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider px-2.5 py-1 rounded-full border ${cfg.className} mb-3`}
      >
        <cfg.icon size={11} />
        {cfg.label}
      </div>
      <p className="text-xs text-neutral-300 leading-relaxed mb-3">
        {data.notes || "—"}
      </p>
      {data.recommendations?.length > 0 && (
        <ul className="space-y-1 text-xs text-neutral-400">
          {data.recommendations.slice(0, 3).map((r) => (
            <li key={r} className="flex gap-2">
              <span className={`w-1 h-1 rounded-full ${cfg.dot} mt-1.5 shrink-0`} />
              <span>{r}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Rapport complet du diagnostic IA : score, 4 domaines, estimations, envoi à GMC. */
export function ReportCard({ report, buildLeadPayload }) {
  const score = Number(report?.global_score) || 0;
  const color = scoreColor(score);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="space-y-4"
      data-testid="diagnosis-report"
    >
      <div className="glass-strong rounded-3xl p-6 md:p-8">
        {/* Score + summary */}
        <div className="flex flex-col sm:flex-row items-start gap-6 mb-8 pb-8 border-b border-white/10">
          <ScoreCircle score={score} color={color} />
          <div className="flex-1">
            <div className="eyebrow mb-2">Diagnostic global</div>
            <div className="font-display text-2xl md:text-3xl text-white leading-tight mb-3">
              {report.summary || "Diagnostic IA terminé"}
            </div>
            <div className="text-sm text-neutral-400">
              Analyse couvrant les 4 domaines clés de votre extérieur.
            </div>
          </div>
        </div>

        {/* Domains */}
        <div className="grid sm:grid-cols-2 gap-3 mb-8">
          {DOMAINS.map((d) => (
            <DomainCard key={d.key} domain={d} data={report.domains?.[d.key] || {}} />
          ))}
        </div>

        {/* Estimations */}
        <div className="grid sm:grid-cols-2 gap-4 mb-8">
          <EstimateCard
            icon={Euro}
            label="Estimation des travaux"
            range={report.cost_estimate}
            accent="text-[#FF5A00]"
            testId="diagnosis-cost"
          />
          <EstimateCard
            icon={TrendingUp}
            label="Gain de valeur immobilière"
            range={report.value_gain_estimate}
            accent="text-emerald-400"
            testId="diagnosis-value-gain"
          />
        </div>

        <p className="text-xs text-neutral-500 text-center mt-3">
          Estimations indicatives basées sur une analyse IA. Visite technique gratuite
          pour chiffrage précis.
        </p>
      </div>

      {/* Panneau d'envoi du rapport à GMC — 4 champs + succès */}
      <QuickLeadPanel
        testIdPrefix="diagnosis-quick-lead"
        ctaEyebrow="Rappel gratuit sous 24 h"
        ctaLabel="📄 Envoyer mon rapport à GMC"
        ctaTitle="Envoyez votre rapport à GMC"
        ctaSubtitle="Un conseiller vous rappelle rapidement avec un devis personnalisé basé sur votre diagnostic."
        formTitle="Vos coordonnées"
        formSubtitle="Un conseiller GMC Rénovation Façade vous contactera rapidement."
        formHelper="Vos photos, le rapport complet du diagnostic (score, 4 domaines, recommandations, estimations) seront joints automatiquement."
        successTitle="Votre rapport a bien été envoyé"
        successBody="Un conseiller GMC Rénovation Façade vous contactera rapidement."
        buildPayload={buildLeadPayload}
      />
    </motion.div>
  );
}
